import { Modal, Button } from "react-bootstrap";
import { useEffect, useRef, useState } from "react";
import styles from "./ExportOtpVerify.module.css";
import {
  generateExportOtpService,
  resendExportOtpService,
  verifyExportOtpService,
  viewProfileService,
} from "../Services/ApiServices";
import Loader from "../SharedComponents/Loader/Loader";
import toast from "react-hot-toast";

const OTP_LENGTH = 6;
const RESEND_TIME = 120; // 2 minutes

export default function ExportOtpVerify({
  show,
  reportType, // 1->Billing,2->order list,3->Brochure User, 4-> Kstore settings
  onClose,
  onSuccessVerify,
  title = "Verify OTP to Export CSV",
}) {
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [isLoader, setisLoader] = useState(false);
  const [resetKey, setresetKey] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  const inputsRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    if (show) {
      if (reportType === 4) {
        handleGenerateOtp();
      } else {
        getProfileApiCall();
      }
      setTimeout(() => inputsRef.current[0]?.focus(), 150);
    }
  }, [show]);

  useEffect(() => {
    if (resendTimer === 0 && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, [resendTimer]);

  const handleChange = (value, index) => {
    if (!/^[a-zA-Z0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value; // 🔥 allow small or caps
    setOtp(newOtp);
    setError("");

    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleClose = () => {
    setOtp(Array(OTP_LENGTH).fill(""));
    setError("");
    setResendTimer(0);

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    onClose();
  };

  const startResendTimer = () => {
    setResendTimer(RESEND_TIME);
    timerRef.current = setInterval(() => {
      setResendTimer((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
  };

  const handleVerifyOtp = (otpStr) => {
    setisLoader(true);
    const formData = new FormData();
    formData.append("reset_key", resetKey);
    formData.append("report_type", reportType);
    formData.append("otp", otpStr);

    verifyExportOtpService(formData)
      .then((res) => {
        toast(res.data.msg, { type: "success" });
        onSuccessVerify(res.data.reset_key);
      })
      .catch((err) => {
        const errMsg =
          err?.response?.data?.detail?.[0]?.msg || "Something went wrong!!";
        toast(errMsg, { type: "error" });
        setError(errMsg);
      })
      .finally(() => setisLoader(false));
  };

  const onVerifyOtp = () => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== OTP_LENGTH) {
      setError("Please enter valid OTP");
      return;
    }
    handleVerifyOtp(enteredOtp);
  };

  const resentOtpApiCall = () => {
    setisLoader(true);
    const formData = new FormData();
    formData.append("reset_key", resetKey);
    formData.append("report_type", reportType);

    resendExportOtpService(formData)
      .then((res) => {
        setresetKey(res.data.reset_key);
        toast(res.data.msg, { type: "success" });
        startResendTimer();
      })
      .catch((err) => {
        const errMsg =
          err?.response?.data?.detail?.[0]?.msg || "Something went wrong!!";
        toast(errMsg, { type: "error" });
        setError(errMsg);
      })
      .finally(() => setisLoader(false));
  };

  const handleGenerateOtp = () => {
    const formData = new FormData();
    formData.append("report_type", reportType);

    generateExportOtpService(formData)
      .then((res) => {
        setresetKey(res.data.reset_key);
        toast(res.data.msg, { type: "success" });
        startResendTimer();
      })
      .catch((err) => {
        const errMsg =
          err?.response?.data?.detail?.[0]?.msg || "Something went wrong!!";
        toast(errMsg, { type: "error" });
        setError(errMsg);
      })
      .finally(() => setisLoader(false));
  };

  const getProfileApiCall = () => {
    setisLoader(true);
    viewProfileService()
      .then(({ data }) => {
        if (data.is_otp === 1) {
          handleGenerateOtp();
        } else {
          onClose();
          toast("You don’t have permission to export CSV", { type: "error" });
        }
      })
      .catch((err) => {
        const errMsg =
          err?.response?.data?.detail?.[0]?.msg || "Something went wrong!!";
        toast(errMsg, { type: "error" });
        setError(errMsg);
      })
      .finally(() => setisLoader(false));
  };

  return (
    <>
      <Loader isLoader={isLoader} />

      <Modal
        show={show}
        onHide={handleClose}
        centered
        backdrop="static"
        dialogClassName={styles.otpModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className={styles.infoText}>
            Enter the 6-character OTP sent to your registered mobile number
          </p>

          <div className={styles.otpWrapper}>
            <div className={styles.otpContainer}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputsRef.current[index] = el)}
                  type="text"
                  maxLength={1}
                  autoComplete="off"
                  className={styles.otpInput}
                  value={digit}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                />
              ))}
            </div>
          </div>

          {error && <p className={styles.errorText}>{error}</p>}

          <div className={styles.resend}>
            {resendTimer > 0 ? (
              <span>
                Resend OTP in{" "}
                <strong>
                  {String(Math.floor(resendTimer / 60)).padStart(2, "0")}:
                  {String(resendTimer % 60).padStart(2, "0")}
                </strong>
              </span>
            ) : (
              <>
                Didn’t receive OTP?
                <Button
                  variant="link"
                  className="p-0 ms-1"
                  onClick={resentOtpApiCall}
                >
                  Resend
                </Button>
              </>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onVerifyOtp}>
            Verify OTP
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
